import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { toast } from "react-toastify";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SelectMolecule } from "@molecules/AsyncSelect";
import DateFieldMolecule from "@molecules/DateField";
import SwitchMolecule from "@molecules/Switch";
import ButtonAtom from "@atoms/Button";
import ReportsService, {
  IReportExportStatus,
} from "@services/ReportsService";
import IReportExportOrganism, { IReportExportFormValues } from "./interface";
import createSchema from "./validation";

type ExportPhase = "form" | "preparing" | "ready" | "failed";

const POLL_INTERVAL_MS = 3000;

const ReportExportOrganism = ({ onClose }: IReportExportOrganism) => {
  const [phase, setPhase] = useState<ExportPhase>("form");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [status, setStatus] = useState<IReportExportStatus | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Refs to keep the poller safe against unmount / close mid-poll.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const exportIdRef = useRef<number | null>(null);
  const filenameRef = useRef<string>("");

  const formMethods = useForm<IReportExportFormValues>({
    mode: "onChange",
    defaultValues: {
      all_clients: false,
      client_ids: [],
      date_start: undefined,
      date_end: undefined,
    },
    resolver: yupResolver(createSchema()),
  });

  const { handleSubmit, control, watch } = formMethods;

  const allClients = watch("all_clients");
  const watchedStart = watch("date_start");

  const clearPoll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Cleanup on unmount: stop the timer and block any late setState.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearPoll();
    };
  }, []);

  const triggerDownload = (id: number, filename: string) => {
    ReportsService.downloadExport(id, filename).catch(() => {
      if (!isMountedRef.current) return;
      toast(t`An error occurred, the file could not be downloaded`, {
        type: "error",
      });
    });
  };

  const pollOnce = () => {
    const id = exportIdRef.current;
    if (id == null) return;

    ReportsService.getExportStatus(id)
      .then((res) => {
        if (!isMountedRef.current) return;
        setStatus(res);

        if (res.status === "ready") {
          clearPoll();
          setPhase("ready");
          // Auto-download once when the job becomes ready.
          triggerDownload(id, filenameRef.current);
        } else if (res.status === "failed") {
          clearPoll();
          setErrorMessage(res.error);
          setPhase("failed");
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        clearPoll();
        setErrorMessage(t`We could not check the export status. Please try again.`);
        setPhase("failed");
      });
  };

  const startPolling = () => {
    clearPoll();
    // Kick off immediately, then on an interval.
    pollOnce();
    intervalRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
  };

  const onSubmit = (data: IReportExportFormValues) => {
    const dateStart = moment(data.date_start).format("YYYY-MM-DD");
    const dateEnd = moment(data.date_end).format("YYYY-MM-DD");

    const params = {
      date_start: dateStart,
      date_end: dateEnd,
      ...(data.all_clients ? {} : { client_ids: data.client_ids }),
    };

    filenameRef.current = `reports-${dateStart}_${dateEnd}.zip`;
    setSubmitting(true);
    setErrorMessage(null);

    ReportsService.createExport(params)
      .then((res) => {
        if (!isMountedRef.current) return;
        setSubmitting(false);
        exportIdRef.current = res.id;
        setPhase("preparing");
        startPolling();
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setSubmitting(false);
        toast(t`Generic error`, { type: "error" });
      });
  };

  const handleRetry = () => {
    clearPoll();
    exportIdRef.current = null;
    setStatus(null);
    setErrorMessage(null);
    setPhase("form");
  };

  const handleClose = () => {
    clearPoll();
    if (onClose) {
      onClose();
    }
  };

  if (phase === "preparing") {
    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h3">
          <Trans>Preparing your export…</Trans>
        </Typography>
        <Typography variant="body1">
          <Trans>
            This can take a while. We'll also email you a download link when it's
            ready.
          </Trans>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Trans>
            You can safely close this dialog — the export will keep running.
          </Trans>
        </Typography>
        <ButtonAtom
          variant="outlined"
          onClick={handleClose}
          label={<Trans>Close</Trans>}
        />
      </Stack>
    );
  }

  if (phase === "ready") {
    return (
      <Stack spacing={2} sx={{ py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DownloadIcon color="success" />
          <Typography variant="h3">
            <Trans>Your export is ready</Trans>
          </Typography>
        </Box>
        <Typography variant="body1">
          <Trans>
            The download should start automatically. We've also emailed you a
            download link.
          </Trans>
        </Typography>
        {status && (
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Typography variant="body2">
              <Trans>Total</Trans>: {status.total ?? 0}
            </Typography>
            <Typography variant="body2">
              <Trans>Uploaded</Trans>: {status.uploaded ?? 0}
            </Typography>
            <Typography variant="body2">
              <Trans>Missing</Trans>: {status.missing ?? 0}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <ButtonAtom
            variant="outlined"
            onClick={() =>
              exportIdRef.current != null &&
              triggerDownload(exportIdRef.current, filenameRef.current)
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DownloadIcon fontSize="small" />
                <Trans>Download again</Trans>
              </Box>
            }
          />
          <ButtonAtom onClick={handleClose} label={<Trans>Close</Trans>} />
        </Box>
      </Stack>
    );
  }

  if (phase === "failed") {
    return (
      <Stack spacing={2} sx={{ py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h3">
            <Trans>Export failed</Trans>
          </Typography>
        </Box>
        <Typography variant="body1">
          {errorMessage || <Trans>Something went wrong. Please try again.</Trans>}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <ButtonAtom
            variant="outlined"
            onClick={handleClose}
            label={<Trans>Close</Trans>}
          />
          <ButtonAtom onClick={handleRetry} label={<Trans>Try again</Trans>} />
        </Box>
      </Stack>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SwitchMolecule
              controlName={"all_clients"}
              control={control}
              label={t`All clients`}
            />
          </Grid>
          {!allClients && (
            <Grid item xs={12}>
              <SelectMolecule
                control={control}
                controlName="client_ids"
                storeCollection="clients"
                multiple
                nullable={false}
                emptyValue={[]}
                optionValue={"id"}
                optionLabel={"name"}
                variant={"outlined"}
                label={<Trans>Clients</Trans>}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <DateFieldMolecule
              control={control}
              controlName={"date_start"}
              inputFormat={"dd/MM/yyyy"}
              label={<Trans>From</Trans>}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateFieldMolecule
              control={control}
              controlName={"date_end"}
              minDate={watchedStart}
              inputFormat={"dd/MM/yyyy"}
              label={<Trans>To</Trans>}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "right" }}>
              <ButtonAtom
                type="submit"
                loading={submitting}
                label={submitting ? t`Starting…` : t`Export ZIP`}
              />
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default ReportExportOrganism;
